import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import ImageUploadField from '../../components/ImageUploadField';
import { Plus, Trash2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    url: '',
    media_type: 'image',
    alt: '',
    rally_name: '',
    order: 0
  });

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gallery`);
      setImages(response.data);
    } catch (error) {
      toast.error('Failed to fetch gallery');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.url) {
      toast.error('Please upload or paste an image URL');
      return;
    }
    const token = localStorage.getItem('token');

    try {
      await axios.post(`${API_URL}/api/gallery`, {
        ...formData,
        order: parseInt(formData.order) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Item added');
      setShowModal(false);
      setFormData({ url: '', media_type: 'image', alt: '', rally_name: '', order: 0 });
      fetchImages();
    } catch (error) {
      toast.error('Failed to add image');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/api/gallery/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image deleted');
      fetchImages();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  return (
    <div data-testid="admin-gallery">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6]">GALLERY</h1>
        <button
          onClick={() => setShowModal(true)}
          data-testid="add-image-btn"
          className="btn-primary flex items-center gap-2 text-sm"
        >
          <Plus size={16} />
          Add Image
        </button>
      </div>

      {loading ? (
        <p className="text-[#A19D98]">Loading...</p>
      ) : images.length === 0 ? (
        <p className="text-[#A19D98]">No images yet.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <div
              key={image.id}
              data-testid={`gallery-item-${image.id}`}
              className="relative group aspect-square bg-[#141414] border border-[#2A2A2A] overflow-hidden"
            >
              {image.media_type === 'video' ? (
                <video
                  src={image.url}
                  className="w-full h-full object-cover"
                  muted
                  loop
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img
                  src={image.url}
                  alt={image.alt || 'Gallery image'}
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                {image.rally_name && (
                  <p className="text-[#F2EDE6] text-sm font-['Oswald']">{image.rally_name}</p>
                )}
                <button
                  onClick={() => handleDelete(image.id)}
                  className="p-2 bg-[#FF3B30] text-white rounded"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <div className="bg-[#141414] border border-[#2A2A2A] p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-['Oswald'] text-2xl text-[#F2EDE6]">ADD IMAGE</h2>
              <button onClick={() => setShowModal(false)} className="text-[#A19D98] hover:text-[#F2EDE6]">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <ImageUploadField
                value={formData.url}
                mediaType={formData.media_type}
                onChange={(url, kind) => setFormData((prev) => ({ ...prev, url, media_type: kind || 'image' }))}
                label="Image or Video *"
                testIdPrefix="gallery-image"
                accept="both"
              />

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Alt Text</label>
                <Input
                  name="alt"
                  value={formData.alt}
                  onChange={handleInputChange}
                  placeholder="Describe the image"
                  data-testid="gallery-alt-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Rally Name</label>
                <Input
                  name="rally_name"
                  value={formData.rally_name}
                  onChange={handleInputChange}
                  placeholder="Austin Tour 2026"
                  data-testid="gallery-rally-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Order</label>
                <Input
                  type="number"
                  name="order"
                  value={formData.order}
                  onChange={handleInputChange}
                  data-testid="gallery-order-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="gallery-submit-btn"
                  className="btn-primary flex-1"
                >
                  Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
